import React, { useState, useReducer, useEffect, createRef } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { DateRangePicker } from 'react-dates';
import { DateRangePickerPhrases } from './defaultPhrases';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Button,
  Pagination,
  Icon as SemanticIcon,
  Input,
  Grid,
  Container,
} from 'semantic-ui-react';
import moment from 'moment';

import { Icon } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

import EventItem from './EventItem';
import { datesForDisplay } from '@package/helpers';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { getQueryStringResults } from '@plone/volto/actions';

const messages = defineMessages({
  eventSearchText: {
    id: 'event_search_text',
    defaultMessage: 'Cerca per parola chiave',
  },
  eventSearchStartDate: {
    id: 'event_search_startDate',
    defaultMessage: 'Data inizio',
  },
  eventSearchEndDate: {
    id: 'event_search_endDate',
    defaultMessage: 'Data fine',
  },
  eventSearchEvents: {
    id: 'event_search_events',
    defaultMessage: 'Eventi',
  },
  eventSearchForText: {
    id: 'event_search_for',
    defaultMessage: 'contenenti',
  },
  eventSearchFromDate: {
    id: 'event_search_fromdate',
    defaultMessage: 'dal',
  },
  eventSearchToDate: {
    id: 'event_search_todate',
    defaultMessage: 'al',
  },
  clearText: {
    id: 'clear_text',
    defaultMessage: 'Cancella',
  },
  no_results_found: {
    id: 'no_results_found',
    defaultMessage: 'Nessun risultato',
  },
});

const filtersActions = {
  DATE: 'DATE',
  TEXT: 'TEXT',
  RESET: 'RESET',
};

const getDefaultStartEndDate = (whichEvents) => {
  let d = { startDate: null, endDate: null };
  switch (whichEvents) {
    case 'today':
      d.startDate = moment().startOf('day');
      d.endDate = moment().endOf('day');
      break;
    case 'weekend':
      const friday = 5;
      const sunday = 7;
      const now_weekday = moment().isoWeekday();
      const nextFriday =
        now_weekday < friday
          ? moment().add(friday - now_weekday, 'days')
          : moment();
      const nextSunday =
        now_weekday < sunday
          ? moment().add(sunday - now_weekday, 'days')
          : moment();
      d.startDate = nextFriday.startOf('day');
      d.endDate = nextSunday.endOf('day');
      break;
    case 'month':
      d.startDate = moment().startOf('month');
      d.endDate = moment().endOf('month');
      break;
    default:
      d.startDate = moment().startOf('day');
      break;
  }

  return d;
};

// default edit sidebar filters
const DEFAULT_B_SIZE = 8;
const DEFAULT_LIMIT = 24;
const DEFAULT_SORT_ON = 'start'; // ORDINA PER
const DEFAULT_SORT_ORDER = 'ascending'; // ORDINA INVERSO

const Body = ({ data, isEditMode, onChangeBlock }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const resultsRef = createRef();
  moment.locale(intl.locale);
  const location = useLocation();
  const pathname = flattenToAppURL(location?.pathname).replace('/edit', '');

  /* ********* STATE ********* */
  const [b_size, setB_size] = useState(DEFAULT_B_SIZE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sort_on, setSortOn] = useState(DEFAULT_SORT_ON);
  const [sort_order, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [currentPage, setCurrentPage] = useState(1);

  const [lastSubrequest, setLastSubrequest] = useState(null);
  const [focusedDateInput, setFocusedDateInput] = useState(null);
  const defaultDates = getDefaultStartEndDate(data.whichEvents);
  const [defaultStartDate, setDefaultStartDate] = useState(
    defaultDates.startDate,
  );
  const [defaultEndDate, setDefaultEndDate] = useState(defaultDates.endDate);

  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  const initialFiltersState = {
    date: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
    text: '',
    changedFilters: true,
  };

  /* ********* SELECTOR ********* */
  const querystringResults = useSelector((state) => {
    return state.querystringsearch?.subrequests?.[lastSubrequest];
  });

  const items = useSelector((state) => {
    return state.querystringsearch?.subrequests?.[lastSubrequest]?.items ?? [];
  });

  const loading = useSelector((state) => {
    return (
      state.querystringsearch?.subrequests?.[lastSubrequest]?.loading || false
    );
  });

  /* ********* REDUCERS ********* */
  const filtersReducer = (state = initialFiltersState, action) => {
    let newState = { ...state };

    switch (action.type) {
      case filtersActions.TEXT:
        newState.text = action.value;
        newState.changedFilters = true;
        break;

      case filtersActions.DATE:
        newState.date = {
          startDate: action.value.startDate ?? state.startDate,
          endDate: action.value.endDate ?? state.endDate,
        };
        newState.changedFilters = true;
        break;

      case filtersActions.RESET:
        newState = {
          ...initialFiltersState,
        };

        break;

      default:
        return newState;
    }

    return newState;
  };

  const [{ text, date, changedFilters }, dispatchFilter] = useReducer(
    filtersReducer,
    initialFiltersState,
  );

  /* ********* EFFECTS ********* */
  useEffect(() => {
    if (changedFilters) {
      doRequest(1, intl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, date, changedFilters, b_size, limit, sort_on, sort_order]);

  //changes of sidebar filters
  useEffect(() => {
    setB_size(data?.query?.b_size ?? DEFAULT_B_SIZE);
  }, [data?.query?.b_size]);

  useEffect(() => {
    setLimit(data?.query?.limit ?? DEFAULT_LIMIT);
  }, [data?.query?.limit]);

  useEffect(() => {
    setSortOn(data?.query?.sort_on ?? DEFAULT_SORT_ON);
  }, [data?.query?.sort_on]);

  useEffect(() => {
    setSortOrder(data?.query?.sort_order ?? DEFAULT_SORT_ORDER);
  }, [data?.query?.sort_order]);

  //filters string
  let filtersStrings = [];

  if (date.startDate && date.endDate) {
    const { sameYear, sameMonth, mStart, mEnd } = datesForDisplay(
      date.startDate,
      date.endDate,
      null,
      intl,
      moment,
    );

    filtersStrings.push(
      ` ${intl.formatMessage(messages.eventSearchFromDate)} ${mStart.format(
        sameMonth && sameYear ? 'D' : sameYear ? 'D MMMM' : 'LL',
      )} ${intl.formatMessage(messages.eventSearchToDate)} ${mEnd.format(
        'LL',
      )}`,
    );
  }

  const getQueryFilter = (query, i, o) => {
    if (!query) return '';
    let ret = null;
    query.forEach((f) => {
      if (i && f.i === i && o && o === f.o) {
        ret = f;
      }
    });
    return ret;
  };

  const getFilters = (intl) => {
    const date_fmt = 'YYYY-MM-DD HH:mm';
    const defaultQuery = data.query?.query ?? [];
    let filters = JSON.parse(JSON.stringify(defaultQuery));

    if (
      !getQueryFilter(
        filters,
        'portal_type',
        'plone.app.querystring.operation.selection.any',
      )
    ) {
      filters.push({
        i: 'portal_type',
        o: 'plone.app.querystring.operation.selection.any',
        v: ['Event'],
      });
    }

    if (text && text.length) {
      filters.push({
        i: 'SearchableText',
        o: 'plone.app.querystring.operation.string.contains',
        v: text + '*',
      });
    }

    if (date.startDate) {
      filters.push({
        i: 'start',
        o: 'plone.app.querystring.operation.date.largerThan',
        v: date.startDate.startOf('day')?.format(date_fmt),
      });
    }

    if (date.endDate) {
      filters.push({
        i: 'end',
        o: 'plone.app.querystring.operation.date.lessThan',
        v: date.endDate.endOf('day')?.format(date_fmt),
      });
    }

    return filters;
  };

  const doRequest = (page = currentPage, intl) => {
    const filters = getFilters(intl);
    const subrequestId = new Date().getTime();
    setLastSubrequest(subrequestId);

    dispatch(
      getQueryStringResults(
        pathname,
        {
          fullobjects: 1,
          query: filters,
          b_size: b_size,
          limit: limit,
          sort_on: sort_on,
          sort_order: sort_order,
        },
        subrequestId,
        page,
      ),
    );
  };

  let isMobile = false;
  if (isClient) isMobile = window && window.innerWidth < 992;

  // Se cambia il tipo di wichEvents o i criteri impostati dalla sidebar (data.query), resetto lo stato dei filtri
  useEffect(() => {
    let defaults = getDefaultStartEndDate(data.whichEvents);
    setDefaultStartDate(defaults.startDate);
    setDefaultEndDate(defaults.endDate);

    dispatchFilter({ type: filtersActions.RESET });
  }, [data.whichEvents, data.query]);

  const handleQueryPaginationChange = (e, { activePage }) => {
    resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    const current = activePage ?? 1;

    setCurrentPage(current);
    doRequest(current, intl);
  };

  return (
    <div ref={resultsRef}>
      <div className="site-search-filters">
        <div className="site-search-filter-text site-search-filter">
          <Input
            placeholder={intl.formatMessage(messages.eventSearchText)}
            aria-label={intl.formatMessage(messages.eventSearchText)}
            aria-controls="events-search-results"
            value={text}
            className={text?.length > 0 ? 'has-value' : ''}
            icon
            onChange={(e, data) => {
              dispatchFilter({
                type: filtersActions.TEXT,
                value: data.value ?? '',
              });
            }}
          >
            <input />
            {text.length > 0 && (
              <Button
                inverted
                icon
                title={intl.formatMessage(messages.clearText)}
                aria-label={intl.formatMessage(messages.clearText)}
                onClick={() =>
                  dispatchFilter({
                    type: filtersActions.TEXT,
                    value: '',
                  })
                }
                aria-controls="events-search-results"
              >
                <Icon name={clearSVG} />
              </Button>
            )}
          </Input>
        </div>

        <div className="site-search-filter dates">
          <DateRangePicker
            startDate={date.startDate}
            startDateId="start-date-filter"
            startDatePlaceholderText={intl.formatMessage(
              messages.eventSearchStartDate,
            )}
            endDate={date.endDate}
            endDateId="end-date-filter"
            endDatePlaceholderText={intl.formatMessage(
              messages.eventSearchEndDate,
            )}
            onDatesChange={({ startDate, endDate }) =>
              dispatchFilter({
                type: filtersActions.DATE,
                value: { startDate, endDate },
              })
            }
            showClearDates={isClient && (!!date.startDate || !!date.endDate)}
            numberOfMonths={isMobile ? 1 : 2}
            minimumNights={0}
            focusedInput={focusedDateInput}
            isOutsideRange={() => false}
            onFocusChange={(focusedInput) => setFocusedDateInput(focusedInput)}
            displayFormat="DD/MM/YYYY"
            phrases={DateRangePickerPhrases(intl)}
            aria-controls="events-search-results"
          />
        </div>
      </div>

      {/* <h2>
        <AnimatePresence>
          <motion.span
            key="items-length"
            className="colored"
          >{`${items_total} `}</motion.span>
          <motion.span key="filters-info">
            {`${intl.formatMessage(messages.eventSearchEvents)}${
              filtersStrings.length > 0 ? ': ' : ''
            }`}
          </motion.span>
          {filtersStrings.length > 0 &&
            filtersStrings.map((filter) => (
              <motion.span
                key={filter}
                initial={{ scale: 0.7, opacity: 0.5 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.7, opacity: 0.5 }}
              >
                {filter}
              </motion.span>
            ))}
        </AnimatePresence>
      </h2> */}

      {/* mostra i risultati solamente dopo che l'utente ha inserito dei filtri (changedFilters) */}

      {loading && changedFilters ? (
        <Container textAlign="center">
          <SemanticIcon loading name="spinner" size="big" />
        </Container>
      ) : (
        <div role="region" aria-live="polite" id="events-search-results">
          {items?.length === 0 && changedFilters && (
            <h2>
              <AnimatePresence>
                <motion.span key="filters-info">
                  {intl.formatMessage(messages.no_results_found)}
                </motion.span>
              </AnimatePresence>
            </h2>
          )}
          {items?.length > 0 && (
            <div className="block listing">
              <Grid
                stackable
                columns={4}
                verticalAlign="top"
                className="default-template"
              >
                <AnimatePresence initial={true}>
                  {items.map((item) => (
                    <motion.div
                      key={item['@id']}
                      className="column"
                      initial={{ scale: 0.7, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.7, opacity: 0 }}
                    >
                      <EventItem item={item} isEditMode={isEditMode} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Grid>

              {querystringResults.total > b_size && (
                <Container textAlign="center">
                  <Pagination
                    activePage={currentPage}
                    totalPages={Math.ceil(querystringResults.total / b_size)}
                    onPageChange={handleQueryPaginationChange}
                    firstItem={null}
                    lastItem={null}
                    prevItem={{
                      content: <Icon name={paginationLeftSVG} size="18px" />,
                      icon: true,
                      'aria-disabled': !querystringResults.batching.prev,
                      className: !querystringResults.batching.prev
                        ? 'disabled'
                        : null,
                    }}
                    nextItem={{
                      content: <Icon name={paginationRightSVG} size="18px" />,
                      icon: true,
                      'aria-disabled': !querystringResults.batching.next,
                      className: !querystringResults.batching.next
                        ? 'disabled'
                        : null,
                    }}
                  />
                </Container>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default Body;
